package com.snakesworld.game.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.rememberTextMeasurer
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.snakesworld.game.domain.NicknameDisplay
import com.snakesworld.game.domain.planLeaderboardColumns
import com.snakesworld.game.engine.LeaderboardEntry
import kotlin.math.max
import com.snakesworld.game.ui.theme.LocalGameCanvasColors
import com.snakesworld.game.ui.theme.appFontFamily
import com.snakesworld.game.viewmodel.LeaderboardViewModel
import org.jetbrains.compose.resources.stringResource
import snakesworld.shared.generated.resources.Res
import snakesworld.shared.generated.resources.block_player
import snakesworld.shared.generated.resources.hidden_player
import snakesworld.shared.generated.resources.kills_header
import snakesworld.shared.generated.resources.report_player
import snakesworld.shared.generated.resources.report_sent
import snakesworld.shared.generated.resources.leaderboard_title
import snakesworld.shared.generated.resources.nickname_header
import snakesworld.shared.generated.resources.rank_header
import snakesworld.shared.generated.resources.score_header

@Composable
fun LeaderboardScreen(
    viewModel: LeaderboardViewModel,
    currentUserId: String = "",
    blockedPlayerIds: Set<String> = emptySet(),
    onReportPlayer: (id: String, name: String) -> Unit = { _, _ -> },
    onBlockPlayer: (id: String) -> Unit = {},
    onBackToMenu: () -> Unit,
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val canvasColors = LocalGameCanvasColors.current
    val hiddenLabel = stringResource(Res.string.hidden_player)
    val visibleEntries = uiState.entries
    var reportedIds by remember { mutableStateOf(setOf<String>()) }

    LaunchedEffect(Unit) {
        viewModel.loadLeaderboard()
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(canvasColors.backgroundColor)
            .windowInsetsPadding(WindowInsets.safeDrawing)
            .padding(16.dp)
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(top = 44.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Header
            Text(
                text = "🏆 ${stringResource(Res.string.leaderboard_title)}",
                color = canvasColors.accentColor,
                fontSize = 24.sp,
                fontWeight = FontWeight.Black,
                fontFamily = appFontFamily(),
                letterSpacing = 2.sp
            )

            Spacer(modifier = Modifier.height(16.dp))

            // Champions Podium (Top 3)
            if (visibleEntries.isNotEmpty()) {
                val top3 = visibleEntries.take(3)
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp),
                    horizontalArrangement = Arrangement.SpaceEvenly,
                    verticalAlignment = Alignment.Bottom
                ) {
                    if (top3.size > 1) PodiumStep(rank = 2, medal = "🥈", entry = top3[1], blockedPlayerIds = blockedPlayerIds, hiddenLabel = hiddenLabel, height = 74.dp, color = Color(0xFFC0C0C0))
                    if (top3.isNotEmpty()) PodiumStep(rank = 1, medal = "🥇", entry = top3[0], blockedPlayerIds = blockedPlayerIds, hiddenLabel = hiddenLabel, height = 96.dp, color = Color(0xFFFFD700))
                    if (top3.size > 2) PodiumStep(rank = 3, medal = "🥉", entry = top3[2], blockedPlayerIds = blockedPlayerIds, hiddenLabel = hiddenLabel, height = 58.dp, color = Color(0xFFCD7F32))
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            val rankHeader = stringResource(Res.string.rank_header)
            val nicknameHeader = stringResource(Res.string.nickname_header)
            val scoreHeader = stringResource(Res.string.score_header)
            val killsHeader = stringResource(Res.string.kills_header)
            ChampionColumns(
                rankHeader = rankHeader,
                nicknameHeader = nicknameHeader,
                scoreHeader = scoreHeader,
                killsHeader = killsHeader,
                modifier = Modifier.weight(1f).fillMaxWidth(),
            ) { columns ->
                Column(modifier = Modifier.fillMaxSize()) {
                    HeaderRow(
                        rankHeader = rankHeader,
                        nicknameHeader = nicknameHeader,
                        scoreHeader = scoreHeader,
                        killsHeader = killsHeader,
                        columns = columns,
                        canvasColors = canvasColors,
                    )

            // Standings List
            LazyColumn(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxWidth()
                    .background(canvasColors.hudBgColor)
                    .border(1.dp, canvasColors.gridColor)
            ) {
                itemsIndexed(visibleEntries) { index, entry ->
                    val isEven = index % 2 == 0
                    val isMe = entry.id == currentUserId && currentUserId.isNotBlank()
                    val blocked = entry.id in blockedPlayerIds
                    val shownName = NicknameDisplay.visibleName(
                        raw = entry.name,
                        playerId = entry.id,
                        blockedIds = blockedPlayerIds,
                        hiddenLabel = hiddenLabel,
                    )
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(if (isEven) Color.Transparent else canvasColors.gridColor.copy(alpha = 0.25f))
                            .padding(horizontal = 12.dp, vertical = 10.dp),
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            "#${index + 1}",
                            color = canvasColors.hudTextColor,
                            fontSize = 12.sp,
                            fontFamily = appFontFamily(),
                            fontWeight = FontWeight.Bold,
                            maxLines = 1,
                            softWrap = false,
                            modifier = Modifier.width(columns.rank),
                        )
                        Column(modifier = Modifier.width(columns.nickname)) {
                            EllipsizedNickname(
                                text = shownName,
                                color = if (blocked) canvasColors.hudTextColor.copy(alpha = 0.55f) else Color.White,
                                fontSize = 13.sp,
                                modifier = Modifier.fillMaxWidth(),
                            )
                            if (!isMe && !blocked) {
                                Row(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                                    Text(
                                        text = stringResource(if (entry.id in reportedIds) Res.string.report_sent else Res.string.report_player),
                                        color = canvasColors.accentColor,
                                        fontSize = 10.sp,
                                        fontFamily = appFontFamily(),
                                        fontWeight = FontWeight.Bold,
                                        maxLines = 1,
                                        softWrap = false,
                                        overflow = TextOverflow.Ellipsis,
                                        modifier = Modifier.clickable {
                                            if (entry.id !in reportedIds) {
                                                onReportPlayer(entry.id, entry.name)
                                                reportedIds = reportedIds + entry.id
                                            }
                                        },
                                    )
                                    Text(
                                        text = stringResource(Res.string.block_player),
                                        color = canvasColors.boundaryColor,
                                        fontSize = 10.sp,
                                        fontFamily = appFontFamily(),
                                        fontWeight = FontWeight.Bold,
                                        maxLines = 1,
                                        softWrap = false,
                                        overflow = TextOverflow.Ellipsis,
                                        modifier = Modifier.clickable {
                                            onBlockPlayer(entry.id)
                                        },
                                    )
                                }
                            }
                        }
                        Text(
                            "${entry.score}",
                            color = canvasColors.accentColor,
                            fontSize = 13.sp,
                            fontFamily = appFontFamily(),
                            fontWeight = FontWeight.Black,
                            maxLines = 1,
                            softWrap = false,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.width(columns.score),
                        )
                        Text(
                            "${entry.kills}",
                            color = canvasColors.boundaryColor,
                            fontSize = 12.sp,
                            fontFamily = appFontFamily(),
                            maxLines = 1,
                            softWrap = false,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.width(columns.kills),
                        )
                    }
                }
            }
            }
            }

        }

        ScreenBackButton(
            onBack = onBackToMenu,
            modifier = Modifier.align(Alignment.TopStart),
            canvasColors = canvasColors,
        )
    }
}

private data class ChampionColumnWidths(
    val rank: Dp,
    val nickname: Dp,
    val score: Dp,
    val kills: Dp,
    val fontSize: androidx.compose.ui.unit.TextUnit,
)

@Composable
private fun ChampionColumns(
    rankHeader: String,
    nicknameHeader: String,
    scoreHeader: String,
    killsHeader: String,
    modifier: Modifier = Modifier,
    content: @Composable (ChampionColumnWidths) -> Unit,
) {
    val density = LocalDensity.current
    val measurer = rememberTextMeasurer()
    val family = appFontFamily()
    BoxWithConstraints(modifier = modifier) {
        val horizontalPadPx = with(density) { 12.dp.roundToPx() * 2 }
        val gapPx = with(density) { 8.dp.roundToPx() }
        val available = (constraints.maxWidth - horizontalPadPx).coerceAtLeast(0)
        var fontSp = 11f
        var plan = planLeaderboardColumns(available, 0, 0, 0, 0, gapPx)
        while (fontSp >= 7f) {
            val style = TextStyle(
                fontFamily = family,
                fontSize = fontSp.sp,
                fontWeight = FontWeight.Bold,
            )
            fun widthOf(text: String): Int {
                val measured = measurer.measure(
                    text = text,
                    style = style,
                    softWrap = false,
                    maxLines = 1,
                    overflow = TextOverflow.Clip,
                ).size.width
                return measured + with(density) { 2.dp.roundToPx() }
            }
            plan = planLeaderboardColumns(
                availablePx = available,
                rankPx = max(widthOf(rankHeader), widthOf("#999")),
                nicknameHeaderPx = widthOf(nicknameHeader),
                scorePx = max(widthOf(scoreHeader), widthOf("999999")),
                killsPx = max(widthOf(killsHeader), widthOf("999")),
                gapPx = gapPx,
            )
            if (plan.fitsWithoutClippingHeaders) break
            fontSp -= 0.5f
        }
        content(
            ChampionColumnWidths(
                rank = with(density) { plan.rankPx.toDp() },
                nickname = with(density) { plan.nicknamePx.toDp() },
                score = with(density) { plan.scorePx.toDp() },
                kills = with(density) { plan.killsPx.toDp() },
                fontSize = fontSp.sp,
            ),
        )
    }
}

@Composable
private fun HeaderRow(
    rankHeader: String,
    nicknameHeader: String,
    scoreHeader: String,
    killsHeader: String,
    columns: ChampionColumnWidths,
    canvasColors: com.snakesworld.game.ui.theme.GameCanvasColors,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(canvasColors.hudBgColor, RoundedCornerShape(topStart = 6.dp, topEnd = 6.dp))
            .padding(horizontal = 12.dp, vertical = 8.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        HeaderLabel(rankHeader, columns.rank, columns.fontSize, canvasColors.hudTextColor.copy(alpha = 0.7f))
        HeaderLabel(nicknameHeader, columns.nickname, columns.fontSize, canvasColors.hudTextColor.copy(alpha = 0.7f))
        HeaderLabel(scoreHeader, columns.score, columns.fontSize, canvasColors.accentColor)
        HeaderLabel(killsHeader, columns.kills, columns.fontSize, canvasColors.boundaryColor)
    }
}

@Composable
private fun HeaderLabel(
    text: String,
    width: Dp,
    fontSize: androidx.compose.ui.unit.TextUnit,
    color: Color,
) {
    Text(
        text = text,
        color = color,
        fontSize = fontSize,
        fontFamily = appFontFamily(),
        fontWeight = FontWeight.Bold,
        maxLines = 1,
        softWrap = false,
        overflow = TextOverflow.Clip,
        modifier = Modifier.width(width),
    )
}

@Composable
private fun PodiumStep(
    rank: Int,
    medal: String,
    entry: LeaderboardEntry,
    blockedPlayerIds: Set<String>,
    hiddenLabel: String,
    height: Dp,
    color: Color,
) {
    val shownName = NicknameDisplay.visibleName(
        raw = entry.name,
        playerId = entry.id,
        blockedIds = blockedPlayerIds,
        hiddenLabel = hiddenLabel,
    )
    Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.width(84.dp)) {
        Text(medal, fontSize = 24.sp)
        EllipsizedNickname(
            text = shownName,
            color = if (entry.id in blockedPlayerIds) Color.White.copy(alpha = 0.55f) else Color.White,
            fontSize = 11.sp,
            modifier = Modifier.fillMaxWidth(),
            textAlign = TextAlign.Center,
        )
        Text("${entry.score}", color = color, fontSize = 12.sp, fontFamily = appFontFamily(), fontWeight = FontWeight.Black, maxLines = 1, softWrap = false)
        Spacer(modifier = Modifier.height(4.dp))
        Box(
            modifier = Modifier
                .width(84.dp)
                .height(height)
                .background(color.copy(alpha = 0.25f), RoundedCornerShape(topStart = 8.dp, topEnd = 8.dp))
                .border(2.dp, color, RoundedCornerShape(topStart = 8.dp, topEnd = 8.dp)),
            contentAlignment = Alignment.Center
        ) {
            Text("#$rank", color = color, fontSize = 20.sp, fontWeight = FontWeight.Black, fontFamily = appFontFamily())
        }
    }
}
